<template>
  <div class="items-filter">
    <div v-if="selectedItem.name" class="selected-item-block">
      <p>{{ selectedItem.name }}</p>
      <span class="kw kw-x" @click.stop="clearFilter" />
    </div>
    <kw-button
      v-else
      :id="triggerId"
      :data-cy="dataCy"
      :append-icon-class="appendIcon"
      :button-text="buttonText"
      button-type="secondary"
      :class="buttonComponentClasses"
      :button-disabled="buttonDisabled"
      @click="openPopup"
    />
    <div v-if="opened" :id="menuId" data-cy="tags-filter-menu" :class="itemsFilterPopupClasses">
      <search-input
        icon-position="left"
        :search-input-handler="searchItems"
        :search-input-placeholder="$t('search')"
        :search-input-value="itemForSearch"
      />
      <ul class="items-filter__list">
        <li
          v-for="(item, i) in filteredItems"
          :key="i"
          :class="itemsFilterItemClasses(item)"
          @click="handleSelectItem(item)"
        >
          <slot :item="item.name" name="label">
            <span :data-tooltip="item.name" v-text="item.name" />
          </slot>
        </li>
      </ul>
    </div>
    <global-events target="window" @click="clickHandler" />
  </div>
</template>

<script>
export default {
  name: 'ItemsFilter',
  props: {
    selectedItem: {
      type: Object,
      required: true,
    },
    checkCallback: {
      type: Function,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    buttonText: {
      type: String,
      required: true,
    },
    menuId: {
      type: String,
      required: true,
    },
    triggerId: {
      type: String,
      required: true,
    },
    buttonDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearFilter: {
      type: Function,
      required: false,
      default: () => {},
    },
    dataCy: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      itemForSearch: '',
      opened: false,
      topPopup: false,
    };
  },
  computed: {
    filteredItems() {
      return this.items.filter((item) => item.name.toLowerCase().includes(this.itemForSearch.toLowerCase()));
    },
    buttonComponentClasses() {
      return {
        'button-component_active': this.opened,
        'button-component_filled': this.selectedItem.id,
      };
    },
    itemsFilterPopupClasses() {
      return {
        'items-filter__popup': true,
        'items-filter__popup_top': this.topPopup,
      };
    },
    appendIcon() {
      return !this.buttonDisabled ? 'kw kw-chevron-down' : '';
    },
  },
  watch: {
    filteredItems() {
      this.updatePopupPosition();
    },
  },
  methods: {
    openPopup() {
      this.opened = !this.opened;
      this.updatePopupPosition();
    },
    updatePopupPosition() {
      setTimeout(() => {
        const trigger = document.getElementById(this.triggerId);
        const menu = document.getElementById(this.menuId);

        if (!menu) {
          return;
        }

        const freeSpace = document.body.offsetHeight - trigger.getBoundingClientRect().bottom;
        this.topPopup = menu.offsetHeight + 6 > freeSpace;
      }, 0);
    },
    isItemChecked(item) {
      return this.selectedItem.id === item.id;
    },
    clickHandler(e) {
      // old drop-menus that do not contain doc click event handler
      if (!this.menuId) {
        return;
      }

      // for lists of items (turn off handling of doc click event if target is trigger)
      if (
        e.target.id === this.triggerId ||
        (document.getElementById(this.triggerId) && document.getElementById(this.triggerId).contains(e.target))
      ) {
        return;
      }

      if (!document.getElementById(this.menuId)) {
        return;
      }

      // if click target is not drop-menu - close it
      if (!document.getElementById(this.menuId).contains(e.target)) {
        this.close();
      }
    },
    close() {
      this.opened = false;
      this.itemForSearch = '';
    },
    searchItems(value) {
      if (this.opened) {
        this.itemForSearch = value;
      }
    },

    itemsFilterItemClasses(g) {
      return {
        active_item: this.isItemChecked(g),
        'items-filter__item': true,
        'items-filter__item_child': g.type === 'child',
        'items-filter__item_last-child': g.last,
        'items-filter__item_disabled': g.deleted,
      };
    },
    handleSelectItem(item) {
      this.itemForSearch = '';
      this.checkCallback(item);
      this.opened = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.items-filter {
  position: relative;

  .selected-item-block {
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding: 0 10px;
    border: 1px solid #dee5ec;
    border-radius: 4px;

    p {
      margin-bottom: 0;
    }
  }

  .kw-x {
    font-size: 8px;
    cursor: pointer;
  }
}

::v-deep .kw-button {
  width: 100%;
  margin-top: 12px;
  border: 1px solid #afbac7 !important;
  box-shadow: none !important;
  justify-content: space-between !important;
  color: kw-color(kw-black, 1) !important;
  font-weight: 400 !important;

  .kw-chevron-down {
    color: kw-color(kw-black, 1) !important;
    font-size: 6px;
    transform: scale(-1);
    font-size: 10px;
  }

  &.button-component_active {
    border-color: #4f4bcb !important;
    .kw-chevron-down {
      transform: scale(1);
    }
  }
}

.items-filter__popup {
  background-color: kw-color(kw-white, 1);
  border-radius: 4px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.2);
  margin-bottom: 6px;
  margin-top: 6px;
  padding: 8px;
  position: absolute;
  right: 0;
  width: 290px;
  z-index: 2;
}

.items-filter__popup_top {
  bottom: 100%;
}

.items-filter__list {
  @extend .custom-scroll;
  margin: 10px 0 0;
  max-height: 166px;
  overflow-y: auto;
  padding: 0;
  width: 100%;
}

.items-filter__item {
  align-items: center;
  color: #383b46;
  display: flex;
  line-height: 16px;
  list-style-type: none;
  padding: 4px 0;
  position: relative;
  border-bottom: 1px solid #e8edf2;
  opacity: 1;
  cursor: pointer;
  transition: opacity 0.3s;

  &:first-child {
    font-weight: 600;
  }

  &:last-child {
    padding-bottom: 0;
    border: none;
  }

  span {
    @import '~sass/cutting-name';
    cursor: pointer;
    margin-left: 8px;
  }

  &:hover {
    opacity: 0.7;
  }
}

.items-filter__item_child {
  padding-left: 24px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: 8px;
    height: 15px;
    border-left: solid 1px #cccccc;
    border-bottom: solid 1px #cccccc;
    top: -6px;
    left: 8px;
  }

  &:after {
    content: '';
    position: absolute;
    width: 8px;
    height: 20px;
    border-left: solid 1px #cccccc;
    left: 8px;
    bottom: 0;
  }
}

.items-filter__item_last-child {
  &:after {
    content: none;
  }
}

.items-filter__item_disabled {
  color: #7b7e86;
  pointer-events: none;
}

.active_item {
  background: #e8edf2;
}
</style>
